import { Dispatch, SetStateAction } from 'react'
import clsx from 'clsx'
import Select from 'react-select'
import { SortableList, SortableItem } from '@thaddeusjiang/react-sortable-list'
import '@thaddeusjiang/react-sortable-list/dist/index.css'
import { useThemeMode } from '../../../../../../_metronic/partials'
import { SelectStyles } from '../../../../../../components/Select'
import { KTIcon } from '../../../../../../_metronic/helpers'
import type { AdapterRow, TrafficType } from '../model/types'
import { TRAFFIC_OPTIONS } from '../labels'

interface Props {
  rows: AdapterRow[]
  setRows: Dispatch<SetStateAction<AdapterRow[]>>
}

const DragHandler = (props: any) => (
  <div title='Переместить' {...props} className='text-gray-400' style={{ cursor: 'grab' }}>
    <KTIcon iconName='abstract-14' className='fs-2' />
  </div>
)

interface SortableRow extends AdapterRow {
  id: string
}

/**
 * Editor table (Экран B). Every routing setting is on view — rank (drag),
 * adapter, active, min ₽, max ₽, traffic split, status — with live inline
 * validation (min>max). Drag-reorder mirrors the existing priority screens
 * (@thaddeusjiang/react-sortable-list). Array order == priority.
 *
 * CHC-873: «Источник» column removed — source is a context axis (one value for
 * the whole configuration), not a per-adapter setting. It's applied from the
 * context on save (see toPriorities), not edited per row.
 */
export const AdapterTable = ({ rows, setRows }: Props) => {
  const { mode: theme } = useThemeMode()

  const items: SortableRow[] = rows.map((r) => ({ ...r, id: String(r.adapterId) }))

  // @thaddeusjiang/react-sortable-list calls setItems with a functional updater
  // (prev => arrayMove(prev, ...)), not a plain array. Rebuild the id-carrying
  // items from the current rows, apply the updater, then strip id back to rows —
  // so the drag actually reorders (and the new order is what gets saved).
  const setItems = (next: SetStateAction<SortableRow[]>) => {
    setRows((prevRows) => {
      const prevItems: SortableRow[] = prevRows.map((r) => ({ ...r, id: String(r.adapterId) }))
      const nextItems = typeof next === 'function' ? next(prevItems) : next
      return nextItems.map(({ id, ...rest }) => rest)
    })
  }

  const patch = (adapterId: number, p: Partial<AdapterRow>) =>
    setRows((prev) => prev.map((r) => (r.adapterId === adapterId ? { ...r, ...p } : r)))

  return (
    <div className='table-responsive'>
      {/* Header row aligned with the body grid. */}
      <div
        className='d-flex align-items-center text-gray-500 text-uppercase fs-8 fw-bold px-3 mb-2'
        style={{ gap: '12px' }}
      >
        <span style={{ width: 28 }} />
        <span style={{ width: 36 }}>#</span>
        <span style={{ minWidth: 160, flex: 1 }}>Адаптер</span>
        <span style={{ width: 70 }}>Активен</span>
        <span style={{ width: 120 }}>Мин ₽</span>
        <span style={{ width: 120 }}>Макс ₽</span>
        <span style={{ width: 180 }}>Сплит трафика</span>
        <span style={{ width: 120 }}>Статус</span>
      </div>

      <SortableList
        items={items as any}
        // @ts-ignore — library setItems typing is loose, matches existing screens
        setItems={setItems}
      >
        {({ items: rendered }: any) => (
          <div className='d-flex flex-column gap-2'>
            {(rendered as SortableRow[]).map((row, index) => {
              const invalid = row.minDeposit > row.maxDeposit
              const disabledGlobally = !row.globallyEnabled
              const active = row.enabled && row.globallyEnabled
              return (
                <SortableItem
                  key={row.id}
                  id={row.id}
                  DragHandler={DragHandler}
                  className='d-flex align-items-center'
                >
                  <div
                    className={clsx(
                      'd-flex align-items-center rounded px-3 py-2 w-100 bg-body',
                      disabledGlobally && 'opacity-75'
                    )}
                    style={{ gap: '12px', border: '1px solid var(--bs-border-color)' }}
                  >
                    <span style={{ width: 36 }} className='fw-bold text-gray-600 text-center'>
                      {index + 1}
                    </span>

                    <div style={{ minWidth: 160, flex: 1 }} className='d-flex flex-column'>
                      <span className='fw-semibold text-gray-900'>{row.name}</span>
                      {disabledGlobally && (
                        <span className='text-danger fs-8'>Отключён глобально</span>
                      )}
                    </div>

                    <div style={{ width: 70 }} className='form-check form-check-solid form-switch'>
                      <input
                        type='checkbox'
                        className='form-check-input w-40px h-25px'
                        style={{ cursor: disabledGlobally ? 'not-allowed' : 'pointer' }}
                        checked={active}
                        disabled={disabledGlobally}
                        onChange={(e) => patch(row.adapterId, { enabled: e.target.checked })}
                      />
                    </div>

                    <div style={{ width: 120 }}>
                      <input
                        type='text'
                        inputMode='numeric'
                        className={clsx('form-control form-control-sm', invalid && 'border-danger')}
                        value={row.minDeposit || ''}
                        placeholder='0'
                        onChange={(e) =>
                          patch(row.adapterId, { minDeposit: toAmount(e.target.value) })
                        }
                      />
                    </div>

                    <div style={{ width: 120, position: 'relative' }}>
                      <input
                        type='text'
                        inputMode='numeric'
                        className={clsx('form-control form-control-sm', invalid && 'border-danger')}
                        value={row.maxDeposit || ''}
                        placeholder='0'
                        onChange={(e) =>
                          patch(row.adapterId, { maxDeposit: toAmount(e.target.value) })
                        }
                      />
                      {invalid && (
                        <span
                          className='text-danger fs-8'
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: 1,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          мин &gt; макс
                        </span>
                      )}
                    </div>

                    <div style={{ width: 180 }}>
                      <Select
                        styles={SelectStyles(theme)}
                        menuPortalTarget={document.body}
                        options={TRAFFIC_OPTIONS}
                        value={
                          TRAFFIC_OPTIONS.find((o) => o.value === row.useTrafficType) ??
                          TRAFFIC_OPTIONS[0]
                        }
                        onChange={(e) =>
                          patch(row.adapterId, {
                            useTrafficType: (e?.value as TrafficType) ?? 'NONE',
                          })
                        }
                      />
                    </div>

                    <div style={{ width: 120 }}>
                      {invalid ? (
                        <span className='badge badge-light-danger'>Конфликт</span>
                      ) : active ? (
                        <span className='badge badge-light-success'>Активен</span>
                      ) : (
                        <span className='badge badge-light-secondary'>Выключен</span>
                      )}
                    </div>
                  </div>
                </SortableItem>
              )
            })}
          </div>
        )}
      </SortableList>
    </div>
  )
}

/**
 * Normalize a free-typed amount field: keep digits only (so the input behaves
 * like a plain text box, not a fiddly type=number with spinners/scroll), floor
 * at 0, no upper bound. Empty input → 0.
 */
function toAmount(value: string): number {
  const digits = value.replace(/\D/g, '')
  return digits === '' ? 0 : Number(digits)
}
